import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGoogleLogin } from 'react-google-login';
import { handleAnalytics, Events, SegmentPageProfileTypes } from '@foxcorp/delta-web-analytics';
import { createProfile, v1Override } from 'helpers/ContentHelper';
import { getCommonTealiumProps } from 'helpers/AccountHelper';
import { removeAnonymousAccountCookie, setAccountCookie } from 'helpers/Account';
import { getIsGoogleAutoSignIn } from 'helpers/AuthHelper';
import { setIsGoogleSignIn, setGoogleOneTap, setNewGoogleEmailAndToken } from 'index/AppActions';
import useStateWithLabel from 'hooks/useStateWithLabel';

export const GOOGLE_CLIENT_ID =
  '32337322301-gc7pf6jm1an6vh4url5mcs851mmea6b9.apps.googleusercontent.com';

export const BTN_GOOGLE_TEXT = 'Continue with Google';

export default function useGetGoogleSignIn(onClose, openCreatePassword) {
  const [response, setResponse] = useStateWithLabel({}, 'response');
  const dispatch = useDispatch();

  const { googleToken, isSignedIn, userGoogleEmailAlreadySignIn } = useSelector(
    state => state.app?.googleSignIn,
  );

  const { isGoogleOneTapDisplayed } = useSelector(state => state.app);

  const getStatus = async email => {
    const { profile } = await createProfile();
    const res = await profile?.status({ email });
    const { found } = res;
    return found;
  };

  const handleSetAccountData = res => {
    setResponse(res);
    removeAnonymousAccountCookie();
    setAccountCookie(res);
    onClose();
  };

  const handleLink = async ({ email, googleToken }) => {
    try {
      const { profile } = await createProfile();
      const res = await profile.googleLink({
        additionalHeaders: v1Override,
        email,
        googleToken,
      });
      handleSetAccountData(res);
    } catch (e) {
      setResponse(e);
    }
  };

  const handleSignIn = async ({ email, googleToken }) => {
    dispatch(setIsGoogleSignIn(true));
    try {
      const { profile } = await createProfile();
      const res = await profile?.googleLogin({
        additionalHeaders: v1Override,
        googleToken,
      });
      handleSetAccountData(res);
    } catch (e) {
      handleLink({ email, googleToken });
      setResponse(e);
    }
  };

  const onSuccess = response => {
    const {
      profileObj: { email },
      tokenId,
    } = response;
    setResponse(response);
    dispatch(setIsGoogleSignIn(true));
    getStatus(email).then(isFound => {
      if (isFound) {
        handleAnalytics({
          event: Events.PROFILE_SIGNIN_COMPLETED,
          payload: {
            page_profile_type: SegmentPageProfileTypes.GOOGLE,
            page_start_source: 'settings',
          },
          tealiumPayload: {
            ...getCommonTealiumProps(),
          },
        });
        handleSignIn({ email, googleToken: tokenId });
      } else {
        dispatch(setNewGoogleEmailAndToken({ googleEmail: email, googleToken: tokenId }));
        openCreatePassword();
      }
    });
  };

  const onFailure = response => {
    setResponse(response);
    dispatch(setIsGoogleSignIn(false));
  };

  useEffect(() => {
    const isAutoSignIn = getIsGoogleAutoSignIn();
    if (isAutoSignIn) {
      if (isGoogleOneTapDisplayed) {
        window?.google?.accounts?.id?.cancel();
        dispatch(setGoogleOneTap({ isGoogleOneTap: false }));
      }
      if (userGoogleEmailAlreadySignIn) {
        getStatus(userGoogleEmailAlreadySignIn).then(isFound => {
          if (isSignedIn && isFound) {
            handleSignIn({ email: userGoogleEmailAlreadySignIn, googleToken });
            handleAnalytics({
              event: Events.PROFILE_SIGNIN_COMPLETED,
              payload: {
                page_profile_type: SegmentPageProfileTypes.GOOGLE,
                page_start_source: 'settings',
              },
              tealiumPayload: {
                ...getCommonTealiumProps(),
              },
            });
          }
        });
      }
    }
  }, []);

  const { signIn } = useGoogleLogin({
    clientId: GOOGLE_CLIENT_ID,
    onFailure,
    onSuccess,
  });

  return {
    BTN_GOOGLE_TEXT,
    dispatch,
    GOOGLE_CLIENT_ID,
    getStatus,
    handleLink,
    handleSignIn,
    handleSetAccountData,
    onFailure,
    onSuccess,
    response,
    signIn,
  };
}
